import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import { Activity, AlertTriangle, Shield, Clock, Zap, Ban, Bell, CheckCircle, FileText, Search, Server, Radar, Eye, Timer, XCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

// Default SLA timelines in hours (must match VulnerabilityHub)
const DEFAULT_SLA_TIMELINES = {
  critical: 24,
  high: 168,
  medium: 720,
  low: 2160,
};

export default function OperationalDashboard() {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [data, setData] = useState(null);
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [detailType, setDetailType] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [slaTimelines, setSlaTimelines] = useState(DEFAULT_SLA_TIMELINES);

  useEffect(() => {
    fetchData();
    loadSlaSettings();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadSlaSettings = () => {
    const saved = localStorage.getItem('sla_timelines');
    if (saved) {
      setSlaTimelines(JSON.parse(saved));
    }
  };

  const fetchData = async () => {
    try {
      const [dashboardRes, vulnsRes] = await Promise.all([
        api.get('/dashboard/operational'),
        api.get('/vulnerabilities')
      ]);
      setData(dashboardRes.data);
      setVulnerabilities(vulnsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch operational dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Calculate SLA status for a vulnerability
  const calculateSlaStatus = (vuln) => {
    if (!vuln.discovered_at) return { status: 'unknown' };
    
    const discoveredDate = new Date(vuln.discovered_at);
    const severity = vuln.severity || 'medium';
    const slaHours = slaTimelines[severity] || slaTimelines.medium;
    const deadline = new Date(discoveredDate.getTime() + slaHours * 60 * 60 * 1000);
    const now = new Date();
    
    const msRemaining = deadline.getTime() - now.getTime();
    const hoursRemaining = msRemaining / (1000 * 60 * 60);
    
    if (vuln.status === 'closed') {
      const closedDate = vuln.closed_at ? new Date(vuln.closed_at) : now;
      return { status: closedDate <= deadline ? 'fixed_on_time' : 'fixed_late' };
    }
    
    if (msRemaining <= 0) return { status: 'overdue' };
    if (hoursRemaining <= 24) return { status: 'critical_soon' };
    if (hoursRemaining <= 72) return { status: 'warning' };
    
    return { status: 'on_track' };
  };

  // Calculate real SLA stats from vulnerabilities
  const slaStats = {
    onTrack: vulnerabilities.filter(v => {
      const sla = calculateSlaStatus(v);
      return ['on_track', 'warning'].includes(sla.status);
    }).length,
    atRisk: vulnerabilities.filter(v => {
      const sla = calculateSlaStatus(v);
      return sla.status === 'critical_soon';
    }).length,
    breached: vulnerabilities.filter(v => {
      const sla = calculateSlaStatus(v);
      return sla.status === 'overdue';
    }).length,
    fixedOnTime: vulnerabilities.filter(v => {
      const sla = calculateSlaStatus(v);
      return sla.status === 'fixed_on_time';
    }).length,
    fixedLate: vulnerabilities.filter(v => {
      const sla = calculateSlaStatus(v);
      return sla.status === 'fixed_late';
    }).length,
  };

  const slaChartData = [
    { name: 'On Track', value: slaStats.onTrack, color: '#22c55e' },
    { name: 'At Risk', value: slaStats.atRisk, color: '#f59e0b' },
    { name: 'Breached', value: slaStats.breached, color: '#ef4444' },
  ].filter(d => d.value > 0);

  // Handle tile clicks for real-time metrics
  const handleMetricClick = async (metricType) => {
    setDetailType(metricType);
    setShowDetailDialog(true);
    setLoadingDetail(true);
    
    try {
      let response;
      switch (metricType) {
        case 'open_vulns':
          response = await api.get('/vulnerabilities?status=open');
          setDetailData({
            title: 'Open Vulnerabilities',
            items: response.data?.slice(0, 10) || [],
            type: 'vulnerabilities'
          });
          break;
        case 'under_remediation':
          response = await api.get('/vulnerabilities?status=under_remediation');
          setDetailData({
            title: 'Under Remediation',
            items: response.data?.slice(0, 10) || [],
            type: 'vulnerabilities'
          });
          break;
        case 'active_threats':
          response = await api.get('/threats?status=active');
          setDetailData({
            title: 'Active Threats',
            items: response.data?.slice(0, 10) || [],
            type: 'threats'
          });
          break;
        case 'high_priority':
          response = await api.get('/vulnerabilities?severity=critical');
          setDetailData({
            title: 'High Priority Queue',
            items: response.data?.slice(0, 10) || [],
            type: 'vulnerabilities'
          });
          break;
        default:
          setDetailData({ title: metricType, items: [], type: 'unknown' });
      }
    } catch (error) {
      setDetailData({ title: 'Error', items: [], type: 'error' });
      toast.error('Failed to fetch details');
    } finally {
      setLoadingDetail(false);
    }
  };

  // Handle SLA tile clicks - navigate to vulnerability hub with filter
  const handleSlaClick = (slaType) => {
    if (slaType === 'breached') {
      navigate('/vulnerabilities', { state: { filter: { sla: 'overdue' } } });
      toast.info('Showing SLA breached vulnerabilities');
    } else if (slaType === 'at_risk') {
      navigate('/vulnerabilities', { state: { filter: { sla: 'critical' } } });
      toast.info('Showing at-risk vulnerabilities');
    } else {
      navigate('/vulnerabilities', { state: { filter: { sla: 'on_track' } } });
      toast.info('Showing on-track remediations');
    }
  };

  // Quick Action handlers
  const quickActions = [
    { 
      label: 'Run Threat Scan', 
      icon: AlertTriangle, 
      color: 'red',
      action: () => {
        navigate('/secureprobe');
        toast.info('Navigating to SecureProbe Scanner');
      }
    },
    { 
      label: 'View Critical Vulns', 
      icon: Shield, 
      color: 'orange',
      action: () => {
        navigate('/vulnerabilities', { state: { filter: { severity: 'critical' } } });
        toast.info('Showing critical vulnerabilities');
      }
    },
    { 
      label: 'Check Compliance', 
      icon: CheckCircle, 
      color: 'blue',
      action: () => {
        navigate('/compliance');
        toast.info('Navigating to Compliance Dashboard');
      }
    },
    { 
      label: 'Generate Report', 
      icon: FileText, 
      color: 'violet',
      action: () => {
        navigate('/reporting');
        toast.info('Navigating to Reports');
      }
    },
    { 
      label: 'Asset Discovery', 
      icon: Server, 
      color: 'cyan',
      action: () => {
        navigate('/assets');
        toast.info('Navigating to Asset Inventory');
      }
    },
    { 
      label: 'Start Assessment', 
      icon: Radar, 
      color: 'emerald',
      action: () => {
        navigate('/assessment-center?tab=schedule');
        toast.info('Navigating to Schedule Assessment');
      }
    },
    { 
      label: 'View Threats', 
      icon: Eye, 
      color: 'amber',
      action: () => {
        navigate('/threat-dashboard');
        toast.info('Navigating to Threat Dashboard');
      }
    },
    { 
      label: 'Search IOCs', 
      icon: Search, 
      color: 'pink',
      action: () => {
        navigate('/threat-dashboard');
        toast.info('Navigating to IOC Search');
      }
    }
  ];

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'text-red-400 bg-red-500/20',
      high: 'text-orange-400 bg-orange-500/20',
      medium: 'text-yellow-400 bg-yellow-500/20',
      low: 'text-green-400 bg-green-500/20',
    };
    return colors[severity] || 'text-zinc-400 bg-zinc-500/20';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className={`font-inter text-xl ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>Loading operational data...</div>
      </div>
    );
  }

  // Real stats from vulnerabilities
  const realStats = {
    openVulns: vulnerabilities.filter(v => v.status === 'open').length,
    underRemediation: vulnerabilities.filter(v => v.status === 'under_remediation').length,
    critical: vulnerabilities.filter(v => v.severity === 'critical' && v.status !== 'closed').length,
  };

  return (
    <div data-testid="operational-dashboard" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`font-inter font-bold text-2xl lg:text-4xl ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            Operational Dashboard
          </h1>
          <p className={`font-inter mt-1 text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Real-time security operations center view</p>
        </div>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${isDarkMode ? 'bg-green-500/20 border border-green-500/30' : 'bg-green-100 border border-green-300'}`}>
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className={`text-xs font-mono ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>LIVE</span>
        </div>
      </div>

      {/* Real-time Metrics - Clickable */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        <div 
          onClick={() => handleMetricClick('open_vulns')}
          className={`rounded-xl p-3 lg:p-4 border-l-4 border-orange-500 cursor-pointer transition-all hover:scale-[1.02] ${isDarkMode ? 'glass hover:bg-white/5' : 'bg-white border border-slate-200 shadow-card hover:shadow-card-hover'}`}
          data-testid="metric-open-vulns"
        >
          <div className="flex items-center gap-2 mb-2">
            <Shield className={`w-4 h-4 lg:w-5 lg:h-5 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} />
            <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Open Vulns</span>
          </div>
          <p className={`text-2xl lg:text-3xl font-inter font-bold ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>{realStats.openVulns}</p>
        </div>
        
        <div 
          onClick={() => handleMetricClick('under_remediation')}
          className={`rounded-xl p-3 lg:p-4 border-l-4 border-yellow-500 cursor-pointer transition-all hover:scale-[1.02] ${isDarkMode ? 'glass hover:bg-white/5' : 'bg-white border border-slate-200 shadow-card hover:shadow-card-hover'}`}
          data-testid="metric-under-remediation"
        >
          <div className="flex items-center gap-2 mb-2">
            <Clock className={`w-4 h-4 lg:w-5 lg:h-5 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
            <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Under Remediation</span>
          </div>
          <p className={`text-2xl lg:text-3xl font-inter font-bold ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>{realStats.underRemediation}</p>
        </div>
        
        <div 
          onClick={() => handleMetricClick('active_threats')}
          className={`rounded-xl p-3 lg:p-4 border-l-4 border-red-500 cursor-pointer transition-all hover:scale-[1.02] ${isDarkMode ? 'glass hover:bg-white/5' : 'bg-white border border-slate-200 shadow-card hover:shadow-card-hover'}`}
          data-testid="metric-active-threats"
        >
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className={`w-4 h-4 lg:w-5 lg:h-5 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
            <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Active Threats</span>
          </div>
          <p className={`text-2xl lg:text-3xl font-inter font-bold ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{data?.active_threats || 0}</p>
        </div>
        
        <div 
          onClick={() => handleMetricClick('high_priority')}
          className={`rounded-xl p-3 lg:p-4 border-l-4 border-indigo-500 cursor-pointer transition-all hover:scale-[1.02] ${isDarkMode ? 'glass hover:bg-white/5' : 'bg-white border border-slate-200 shadow-card hover:shadow-card-hover'}`}
          data-testid="metric-high-priority"
        >
          <div className="flex items-center gap-2 mb-2">
            <Zap className={`w-4 h-4 lg:w-5 lg:h-5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
            <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>High Priority</span>
          </div>
          <p className={`text-2xl lg:text-3xl font-inter font-bold ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>{realStats.critical}</p>
        </div>
      </div>

      {/* SLA Status - Real Data from Vulnerabilities */}
      <div className={`rounded-2xl p-4 lg:p-6 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-card'}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className={`font-inter font-semibold text-lg lg:text-xl flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            <Timer className={`w-5 h-5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
            SLA Status
          </h2>
          <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Based on real vulnerability data</span>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* SLA Cards */}
          <div className="grid grid-cols-3 gap-3 lg:col-span-3">
            <div 
              onClick={() => handleSlaClick('breached')}
              className={`rounded-xl p-3 lg:p-4 text-center cursor-pointer transition-all hover:scale-[1.02] ${isDarkMode ? 'bg-red-500/10 border border-red-500/30 hover:bg-red-500/20' : 'bg-red-50 border border-red-200 hover:bg-red-100'}`}
              data-testid="sla-breached"
            >
              <XCircle className={`w-5 h-5 mx-auto mb-2 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
              <p className={`text-2xl lg:text-3xl font-inter font-bold ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{slaStats.breached}</p>
              <p className={`text-xs lg:text-sm mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>SLA Breached</p>
            </div>
            <div 
              onClick={() => handleSlaClick('at_risk')}
              className={`rounded-xl p-3 lg:p-4 text-center cursor-pointer transition-all hover:scale-[1.02] ${isDarkMode ? 'bg-yellow-500/10 border border-yellow-500/30 hover:bg-yellow-500/20' : 'bg-yellow-50 border border-yellow-200 hover:bg-yellow-100'}`}
              data-testid="sla-at-risk"
            >
              <AlertTriangle className={`w-5 h-5 mx-auto mb-2 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
              <p className={`text-2xl lg:text-3xl font-inter font-bold ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>{slaStats.atRisk}</p>
              <p className={`text-xs lg:text-sm mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>At Risk</p>
            </div>
            <div 
              onClick={() => handleSlaClick('on_track')}
              className={`rounded-xl p-3 lg:p-4 text-center cursor-pointer transition-all hover:scale-[1.02] ${isDarkMode ? 'bg-green-500/10 border border-green-500/30 hover:bg-green-500/20' : 'bg-green-50 border border-green-200 hover:bg-green-100'}`}
              data-testid="sla-on-track"
            >
              <CheckCircle className={`w-5 h-5 mx-auto mb-2 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
              <p className={`text-2xl lg:text-3xl font-inter font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>{slaStats.onTrack}</p>
              <p className={`text-xs lg:text-sm mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>On Track</p>
            </div>
          </div>
          
          {/* Mini Chart */}
          <div className="flex flex-col items-center justify-center">
            <div className="h-24 w-24">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={slaChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={25}
                    outerRadius={40}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {slaChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: isDarkMode ? '#18181b' : '#fff', border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex gap-2 mt-2">
              <span className={`flex items-center gap-1 text-[10px] ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                <div className="w-2 h-2 rounded-full bg-green-500" /> On Track
              </span>
              <span className={`flex items-center gap-1 text-[10px] ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                <div className="w-2 h-2 rounded-full bg-red-500" /> Breached
              </span>
            </div>
          </div>
        </div>
        
        {/* Fixed Stats */}
        <div className={`grid grid-cols-2 gap-3 mt-4 pt-4 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
          <div className={`flex items-center gap-3 rounded-lg p-3 ${isDarkMode ? 'bg-emerald-500/10' : 'bg-emerald-50'}`}>
            <CheckCircle className={`w-5 h-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
            <div>
              <p className={`text-lg font-inter font-bold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>{slaStats.fixedOnTime}</p>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Fixed Within SLA</p>
            </div>
          </div>
          <div className={`flex items-center gap-3 rounded-lg p-3 ${isDarkMode ? 'bg-purple-500/10' : 'bg-purple-50'}`}>
            <Clock className={`w-5 h-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
            <div>
              <p className={`text-lg font-inter font-bold ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>{slaStats.fixedLate}</p>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Fixed After SLA</p>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={`rounded-xl p-4 lg:p-5 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-card'}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-8 h-8 lg:w-10 lg:h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
              <Activity className={`w-4 h-4 lg:w-5 lg:h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            </div>
            <div>
              <p className={`text-xl lg:text-2xl font-inter font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{data?.todays_events || vulnerabilities.length}</p>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Events Today</p>
            </div>
          </div>
          <div className={`h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-200'}`}>
            <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full" style={{ width: '65%' }} />
          </div>
        </div>
        
        <div className={`rounded-xl p-4 lg:p-5 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-card'}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-8 h-8 lg:w-10 lg:h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-orange-500/20' : 'bg-orange-100'}`}>
              <Bell className={`w-4 h-4 lg:w-5 lg:h-5 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} />
            </div>
            <div>
              <p className={`text-xl lg:text-2xl font-inter font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{data?.alerts_24h || realStats.critical}</p>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Alerts (24h)</p>
            </div>
          </div>
          <div className={`h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-200'}`}>
            <div className="h-full bg-gradient-to-r from-orange-500 to-yellow-400 rounded-full" style={{ width: '45%' }} />
          </div>
        </div>
        
        <div className={`rounded-xl p-4 lg:p-5 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-card'}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-8 h-8 lg:w-10 lg:h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-green-500/20' : 'bg-green-100'}`}>
              <Ban className={`w-4 h-4 lg:w-5 lg:h-5 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
            </div>
            <div>
              <p className={`text-xl lg:text-2xl font-inter font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{data?.blocked_attacks || 0}</p>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Attacks Blocked</p>
            </div>
          </div>
          <div className={`h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-200'}`}>
            <div className="h-full bg-gradient-to-r from-green-500 to-emerald-400 rounded-full" style={{ width: '98%' }} />
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className={`rounded-2xl p-4 lg:p-6 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-card'}`}>
        <h2 className={`font-inter font-semibold text-lg lg:text-xl mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Quick Actions</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 lg:gap-3">
          {quickActions.map((action, idx) => (
            <button
              key={idx}
              onClick={action.action}
              data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`p-3 lg:p-4 rounded-lg border transition-all hover:scale-105 active:scale-95 ${
                isDarkMode 
                  ? (action.color === 'red' ? 'bg-red-500/10 border-red-500/30 hover:bg-red-500/20' :
                     action.color === 'orange' ? 'bg-orange-500/10 border-orange-500/30 hover:bg-orange-500/20' :
                     action.color === 'blue' ? 'bg-blue-500/10 border-blue-500/30 hover:bg-blue-500/20' :
                     action.color === 'violet' ? 'bg-violet-500/10 border-violet-500/30 hover:bg-violet-500/20' :
                     action.color === 'cyan' ? 'bg-cyan-500/10 border-cyan-500/30 hover:bg-cyan-500/20' :
                     action.color === 'emerald' ? 'bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/20' :
                     action.color === 'amber' ? 'bg-amber-500/10 border-amber-500/30 hover:bg-amber-500/20' :
                     'bg-pink-500/10 border-pink-500/30 hover:bg-pink-500/20')
                  : (action.color === 'red' ? 'bg-red-50 border-red-200 hover:bg-red-100' :
                     action.color === 'orange' ? 'bg-orange-50 border-orange-200 hover:bg-orange-100' :
                     action.color === 'blue' ? 'bg-blue-50 border-blue-200 hover:bg-blue-100' :
                     action.color === 'violet' ? 'bg-violet-50 border-violet-200 hover:bg-violet-100' :
                     action.color === 'cyan' ? 'bg-cyan-50 border-cyan-200 hover:bg-cyan-100' :
                     action.color === 'emerald' ? 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100' :
                     action.color === 'amber' ? 'bg-amber-50 border-amber-200 hover:bg-amber-100' :
                     'bg-pink-50 border-pink-200 hover:bg-pink-100')
              }`}
            >
              <action.icon className={`w-5 h-5 lg:w-6 lg:h-6 mb-1 lg:mb-2 mx-auto ${
                isDarkMode
                  ? (action.color === 'red' ? 'text-red-400' :
                     action.color === 'orange' ? 'text-orange-400' :
                     action.color === 'blue' ? 'text-blue-400' :
                     action.color === 'violet' ? 'text-violet-400' :
                     action.color === 'cyan' ? 'text-cyan-400' :
                     action.color === 'emerald' ? 'text-emerald-400' :
                     action.color === 'amber' ? 'text-amber-400' :
                     'text-pink-400')
                  : (action.color === 'red' ? 'text-red-600' :
                     action.color === 'orange' ? 'text-orange-600' :
                     action.color === 'blue' ? 'text-blue-600' :
                     action.color === 'violet' ? 'text-violet-600' :
                     action.color === 'cyan' ? 'text-cyan-600' :
                     action.color === 'emerald' ? 'text-emerald-600' :
                     action.color === 'amber' ? 'text-amber-600' :
                     'text-pink-600')
              }`} />
              <p className={`text-[10px] lg:text-xs text-center ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>{action.label}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className={`max-w-lg max-h-[80vh] overflow-y-auto ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}`}>
          <DialogHeader>
            <DialogTitle className={`font-inter text-xl ${isDarkMode ? '' : 'text-slate-900'}`}>{detailData?.title || 'Details'}</DialogTitle>
          </DialogHeader>
          {loadingDetail ? (
            <div className="flex items-center justify-center py-8">
              <div className={`w-8 h-8 border-2 border-t-transparent rounded-full animate-spin ${isDarkMode ? 'border-indigo-400' : 'border-indigo-600'}`} />
            </div>
          ) : detailData?.items?.length > 0 ? (
            <div className="space-y-3">
              {detailData.items.map((item, idx) => (
                <div key={item.id || idx} className={`rounded-lg p-3 ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{item.title || item.name}</p>
                      <p className={`text-xs mt-1 line-clamp-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{item.description}</p>
                    </div>
                    {item.severity && (
                      <span className={`px-2 py-0.5 rounded text-xs ${getSeverityColor(item.severity)}`}>
                        {item.severity}
                      </span>
                    )}
                  </div>
                </div>
              ))}
              <Button 
                className="w-full mt-4 bg-indigo-600 hover:bg-indigo-500"
                onClick={() => {
                  setShowDetailDialog(false);
                  navigate('/vulnerabilities');
                }}
              >
                View All
              </Button>
            </div>
          ) : (
            <p className={`text-center py-4 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>No items found</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
